"use client";

import React, { useCallback, useContext, useMemo } from "react";
import styled, { keyframes, ThemeContext } from "styled-components";
import { getColor } from "../utils";
import Box, { BoxProps } from "../Box/Box";
import BaseCircle from "./components/BaseCircle";

const spinning = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(960deg);
  }
`;

const Spinner = styled(Box).attrs({
  position: "relative",
  display: "block",
})<{ center: boolean }>`
  pointer-events: none;
  transform: ${props => (props.center ? "translateY(-50%)" : null)};

  & svg {
    animation: ${spinning} 1.5s ease-in-out infinite alternate;
    transform-origin: 50% 50%;
  }

  & svg::selection {
    background: transparent;
  }
`;

const sizes = {
  extraSmall: 16,
  small: 24,
  default: 40,
  large: 100,
};

const strokeWidths = {
  extraSmall: 3, // 2px visually at 16px size with 24px viewBox
  small: 2, // 2px visually at 24px size with 24px viewBox
  default: 1.8, // 3px visually at 40px size with 24px viewBox
  large: 1.92, // 8px visually at 100px size with 24px viewBox
};

type Props = {
  center?: boolean;
  size?: "extraSmall" | "small" | "default" | "large";
  // This component is themed, customizing color is not recommended
  color?: string;
  indeterminate?: boolean;
  progress?: number;
} & BoxProps;

const ActivityIndicator: React.FC<Props> = ({
  size,
  center,
  color,
  indeterminate = true,
  progress,
  ...styling
}) => {
  const theme = useContext(ThemeContext);
  const _size = sizes[size] || sizes.default;
  const strokeWidth = strokeWidths[size] || strokeWidths.default;
  const radius = 10;
  const circumference = 2 * Math.PI * radius;

  const spinnerProps = useMemo(
    () =>
      center
        ? {
            m: "auto",
            width: "100%",
            top: "50%",
            textAlign: "center",
            height: null,
          }
        : {
            height: _size,
            width: _size,
          },
    [center, _size]
  );

  const baseProps = useMemo(
    () => ({
      r: radius,
      strokeWidth,
    }),
    [radius, strokeWidth]
  );

  const renderProgressIndicator = useCallback(() => {
    const offset = circumference * (1 - progress / 100);
    return (
      <svg width={_size} height={_size} viewBox={`0 0 24 24`} style={{ display: "block" }}>
        <BaseCircle stroke={getColor("gray")} {...baseProps} />
        <BaseCircle
          stroke={getColor(color || (theme && theme.primaryColor), "primary")}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: "stroke-dashoffset 0.3s ease",
          }}
          {...baseProps}
        />
      </svg>
    );
  }, [circumference, color, theme, progress, baseProps, _size]);

  const renderIndeterminate = useCallback(() => {
    return (
      <Spinner role="progressbar" center={center} {...spinnerProps}>
        <svg viewBox={`0 0 24 24`} width={_size} height={_size}>
          <BaseCircle
            {...baseProps}
            stroke={getColor(color || theme?.primaryColor, "primary")}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * 0.25}
          />
        </svg>
      </Spinner>
    );
  }, [center, spinnerProps, _size, baseProps, color, theme?.primaryColor, circumference]);

  return (
    <Box
      height={center ? "100%" : _size}
      width={center ? "100%" : _size}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
      aria-valuenow={indeterminate ? undefined : progress}
      {...styling}
      data-testid="loading">
      {indeterminate ? renderIndeterminate() : renderProgressIndicator()}
    </Box>
  );
};

export default ActivityIndicator;
